import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FaMapMarkerAlt, FaParking, FaClock, FaMobileAlt, FaCamera, FaShieldAlt } from 'react-icons/fa';
import AnimatedCar from './AnimatedCar';

// Background animation
const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const FeaturesContainer = styled.section`
  background: linear-gradient(135deg, #0c1f25 0%, #143b43 50%, #0c1f25 100%);
  background-size: 200% 200%;
  animation: ${css`${gradientAnimation}`} 15s ease infinite;
  padding: 5rem 2rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at center, rgba(0, 225, 199, 0.05) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const glowAnimation = keyframes`
  0% { text-shadow: 0 0 10px rgba(0, 225, 199, 0.5); }
  50% { text-shadow: 0 0 20px rgba(0, 225, 199, 0.8), 0 0 30px rgba(0, 225, 199, 0.4); }
  100% { text-shadow: 0 0 10px rgba(0, 225, 199, 0.5); }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 3rem;
  margin-bottom: 4rem;
  color: white;
  position: relative;
  animation: ${css`${glowAnimation}`} 3s ease-in-out infinite;
  
  &::after {
    content: '';
    position: absolute;
    width: 120px;
    height: 4px;
    background: linear-gradient(to right, #fff, #00e1c7);
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2rem;
`;

const CarContainer = styled.div`
  margin-top: 1rem;
  transform: scale(1.2);
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const cardHoverAnimation = keyframes`
  0% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-5px) scale(1.02); }
  100% { transform: translateY(0) scale(1); }
`;

const FeatureCard = styled.div`
  background: linear-gradient(145deg, #0c1f25, #143b43);
  border-radius: 20px;
  padding: 2.5rem 2rem;
  text-align: center;
  transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(0, 225, 199, 0.1);
  
  &::before {
    content: '';
    position: absolute;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle at center, rgba(0, 225, 199, 0.1) 0%, transparent 60%);
    top: -50%;
    left: -50%;
    opacity: 0;
    transition: opacity 0.5s ease;
  }
  
  &:hover {
    transform: translateY(-15px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
    border: 1px solid rgba(0, 225, 199, 0.3);
    
    &::before {
      opacity: 1;
    }
    
    animation: ${css`${cardHoverAnimation}`} 3s ease-in-out infinite;
  }
`;

const iconAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

const FeatureIcon = styled.div`
  font-size: 3.5rem;
  color: #00e1c7;
  margin-bottom: 1.5rem;
  text-shadow: 0 0 15px rgba(0, 225, 199, 0.7);
  transition: all 0.3s ease;
  
  ${FeatureCard}:hover & {
    animation: ${css`${iconAnimation}`} 2s ease-in-out infinite;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  color: white;
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    background: linear-gradient(to right, transparent, #00e1c7, transparent);
    bottom: -5px;
    left: 50%;
    transform: translateX(-50%);
    transition: width 0.3s ease;
  }
  
  ${FeatureCard}:hover &::after {
    width: 80%;
  }
`;

const FeatureDescription = styled.p`
  color: #bbb;
  font-size: 1.1rem;
  line-height: 1.7;
  transition: color 0.3s ease;
  
  ${FeatureCard}:hover & {
    color: #ddd;
  }
`;

const Features = () => {
  const featuresData = [
    {
      id: 1,
      icon: <FaParking />,
      title: 'Real-time Availability',
      description: 'View real-time parking slot availability through our CCTV-powered monitoring system.'
    },
    {
      id: 2,
      icon: <FaMapMarkerAlt />,
      title: 'Find & Book Parking',
      description: 'Quickly search for available parking spots and book them in advance.'
    },
    {
      id: 3,
      icon: <FaClock />,
      title: 'Save Time',
      description: 'No more circling around looking for parking. Save time and reduce stress.'
    },
    {
      id: 4,
      icon: <FaMobileAlt />,
      title: 'Mobile Friendly',
      description: 'Access all features from our easy-to-use mobile app, available on Android.'
    },
    {
      id: 5,
      icon: <FaCamera />,
      title: 'CCTV Surveillance',
      description: 'Our smart cameras monitor parking slots and update availability in real-time.'
    },
    {
      id: 6,
      icon: <FaShieldAlt />,
      title: 'Secure Parking',
      description: 'Enhanced security with 24/7 surveillance for your vehicle.'
    }
  ];
  
  return (
    <FeaturesContainer id="features">
      <TitleContainer>
        <SectionTitle>App Features</SectionTitle>
        <CarContainer>
          <AnimatedCar />
        </CarContainer>
      </TitleContainer>
      <FeaturesGrid>
        {featuresData.map(feature => (
          <FeatureCard key={feature.id}>
            <FeatureIcon>{feature.icon}</FeatureIcon>
            <FeatureTitle>{feature.title}</FeatureTitle>
            <FeatureDescription>{feature.description}</FeatureDescription>
          </FeatureCard>
        ))}
      </FeaturesGrid>
    </FeaturesContainer>
  );
};

export default Features;